import { CycleOptions } from "@qendo/profile";
import { router, Stack } from "expo-router";
import { useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { ComboBox, type ItemType } from "~/components/form/combo-box/ComboBox";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { OnboardingSkip } from "~/components/layout/home/OnboardingSkip";
import { Page } from "~/components/layout/Page";
import { Colour, Title } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Nothing } from "~/components/permission/Nothing";
import { Flex, Gap, Padding } from "~/constants/layout";
import { useDismiss } from "~/providers/DismissProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";

export default function CycleLength() {
    const { cycle, updateCycleSettings } = useProfile();
    const [predictable, setPredictable] = useState<string>("");
    const { handleDismiss } = useDismiss();
    const [length, setLength] = useState<number>(cycle.cycle);
    const { saving, setSaving } = useFeedback();

    const saveCycle = async () => {
        if (saving) {
            return;
        }

        if (predictable === "Yes") {
            setSaving(true);

            await updateCycleSettings({
                ...cycle,
                cycle: length,
            }).finally(() => setSaving(false));

            if (length <= 1) {
                return handleDismiss();
            }
        }

        router.replace("/on-boarding/period-predictability");
    };

    return (
        <>
            <Stack.Screen
                options={{
                    headerLeft: () => <Nothing />,
                    headerRight: () => <OnboardingSkip next="/on-boarding/period-predictability" />,
                }}
            />
            <Authorize ability="update" section="cycle">
                <Page gradient spacer>
                    <View style={[Flex.between, Flex.grow, Padding.bottom_xxl]}>
                        <View style={[Gap.xxl, Padding.horizontal_xs]}>
                            <RadioSelector
                                title={
                                    <Title size="xl">
                                        Is the length of your <Colour colour="brand">cycle</Colour> predictable?
                                    </Title>
                                }
                                options={["Yes", "No", "I don’t know"]}
                                value={predictable}
                                onValueChange={setPredictable}
                            />
                            {predictable === "Yes" && (
                                <ComboBox<number, ItemType<number>>
                                    label="Cycle length"
                                    value={length}
                                    onItemSelected={setLength}
                                    items={CycleOptions}
                                    searchable
                                />
                            )}
                        </View>
                        <View style={Padding.horizontal_md}>
                            <Button
                                title="Continue"
                                variant="primary"
                                size="lg"
                                disabled={!predictable}
                                onPress={saveCycle}
                            />
                        </View>
                    </View>
                </Page>
            </Authorize>
        </>
    );
}
